import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { QueryKey, useQuery } from 'react-query';

export interface DropdownEntityProps<T> {
  label: string;
  /** When multiple is true, value is string[]; otherwise string | null */
  value?: string | string[] | null;
  queryKey: QueryKey;
  fetchEntities: () => Promise<T[]>;
  getOptionLabel: (option: T) => string;
  getOptionValue?: (option: T) => string;
  /** When multiple is true, value/option are arrays; otherwise single */
  onChange: (value: string | string[] | null, option: T | T[] | null) => void;
  enabled?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  fullWidth?: boolean;
  noOptionsText?: string;
  /** Same as MUI Autocomplete multiple: allow selecting multiple options */
  multiple?: boolean;
  /** Options to prepend to the fetched list (e.g. "Tutti gli usi energetici") */
  prependOptions?: T[];
  /** When set, only options whose id (getOptionValue) is in this list are shown. Prepends are still shown. */
  filterOptionIds?: string[] | null;
}

export function DropdownEntity<T>({
  label,
  value = null,
  queryKey,
  fetchEntities,
  getOptionLabel,
  getOptionValue,
  onChange,
  enabled = true,
  disabled = false,
  required = false,
  error = false,
  helperText,
  placeholder,
  fullWidth = true,
  noOptionsText = 'Nessuna opzione disponibile',
  multiple = false,
  prependOptions,
  filterOptionIds,
}: DropdownEntityProps<T>) {
  const valueExtractor =
    getOptionValue ||
    ((option: any) => {
      if (!option) {
        return '';
      }
      if (typeof option === 'object' && option !== null && 'id' in option) {
        return String((option as any).id ?? '');
      }
      return '';
    });

  const {
    data: fetchedOptions = [],
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery<T[]>(queryKey, fetchEntities, {
    enabled,
    keepPreviousData: true,
  });

  const options = React.useMemo(() => {
    const base = filterOptionIds != null
      ? fetchedOptions.filter(opt => filterOptionIds.includes(valueExtractor(opt)))
      : fetchedOptions;
    return [...(prependOptions || []), ...base];
  }, [prependOptions, fetchedOptions, filterOptionIds, valueExtractor]);

  // Quando filterOptionIds cambia, rimuovi dai valori selezionati gli id non più presenti e notifica il parent
  React.useEffect(() => {
    if (filterOptionIds == null) return;
    const allowedSet = new Set(filterOptionIds);
    (prependOptions || []).forEach(opt => allowedSet.add(valueExtractor(opt)));
    const currentIds = multiple
      ? (Array.isArray(value) ? value : value != null ? [value] : [])
      : value != null && !Array.isArray(value)
        ? [value]
        : [];
    const filteredIds = currentIds.filter((id: string) => allowedSet.has(id));
    if (filteredIds.length === currentIds.length) return;
    const filteredOptions = options.filter(opt => filteredIds.includes(valueExtractor(opt)));
    onChange(
      filteredIds.length ? (multiple ? filteredIds : filteredIds[0]) : null,
      filteredOptions.length ? (multiple ? filteredOptions : filteredOptions[0]) : null
    );
  }, [filterOptionIds, value, multiple, prependOptions, options, valueExtractor, onChange]);

  const loading = isLoading || isFetching;

  const selectedOption = React.useMemo(() => {
    if (!options.length) {
      return multiple ? ([] as T[]) : null;
    }
    if (multiple) {
      const ids = Array.isArray(value) ? value : value != null ? [value] : [];
      return options.filter(option => ids.includes(valueExtractor(option))) as T[];
    }
    if (value == null || Array.isArray(value)) {
      return null;
    }
    return options.find(option => valueExtractor(option) === value) ?? null;
  }, [options, value, valueExtractor, multiple]);

  const computedHelperText: React.ReactNode =
    helperText !== undefined
      ? helperText
      : queryError
        ? 'Errore nel caricamento delle opzioni'
        : undefined;

  const handleChange = React.useCallback(
    (_event: React.SyntheticEvent, newValue: T | T[] | null) => {
      if (multiple) {
        const arr = Array.isArray(newValue) ? newValue : newValue != null ? [newValue] : [];
        const ids = arr.map(option => valueExtractor(option));
        onChange(ids.length ? ids : null, arr.length ? arr : null);
      } else {
        const single = Array.isArray(newValue) ? newValue[0] ?? null : newValue;
        const newValueId = single ? valueExtractor(single) : null;
        onChange(newValueId, (single as T | null) ?? null);
      }
    },
    [multiple, onChange, valueExtractor]
  );

  return (
    <Autocomplete<T, boolean>
      multiple={multiple}
      options={options}
      value={selectedOption as T | T[]}
      onChange={handleChange}
      getOptionLabel={option => getOptionLabel(option)}
      isOptionEqualToValue={(option, optionValue) =>
        valueExtractor(option) === valueExtractor(optionValue)
      }
      loading={loading}
      disabled={disabled || !enabled}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error || !!queryError}
          helperText={computedHelperText}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={loading ? 'Caricamento...' : noOptionsText}
      sx={{ width: fullWidth ? '100%' : undefined }}
    />
  );
}

